import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Building, Rocket, Users, MessageCircle } from "lucide-react";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import RelatedResources from "../components/RelatedResources";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20Business%20Registration%20in%20Coimbatore";

const FAQS = [
  { q: "Which business structure should I choose — Proprietorship, LLP, or Pvt Ltd?", a: "Proprietorship suits solo entrepreneurs starting small (low compliance, unlimited liability). LLP is ideal for service-based businesses with 2+ partners (limited liability, moderate compliance). Pvt Ltd is best when you plan to raise external funding, hire employees, or scale (limited liability, higher compliance). Use our Compliance Roadmap to find the right fit in 60 seconds." },
  { q: "Is MSME / Udyam registration mandatory?", a: "Udyam registration is not legally mandatory, but it is strongly recommended for every small business. It is free, takes 5 minutes online, and unlocks priority sector lending, government tender preference, MSME Samadhaan protection against delayed payments, and access to subsidies under various schemes." },
  { q: "How long does company incorporation take?", a: "Private Limited Company incorporation through MCA's SPICe+ form typically takes 7–10 working days. LLP incorporation takes 10–15 days. Proprietorship needs no formal incorporation — you just register for GST, MSME, or Shop & Establishment as applicable." },
  { q: "What is Startup India recognition and who qualifies?", a: "Startup India recognition is granted by DPIIT to entities incorporated as Pvt Ltd, LLP, or Partnership within the last 10 years with annual turnover under ₹100 crore. Benefits include 3-year income tax exemption under Section 80-IAC, self-certification on labour and environmental laws, and fast-track patent filing with up to 80% rebate on fees." },
  { q: "What documents are needed for company incorporation?", a: "PAN and Aadhaar of all directors/partners, passport-size photos, proof of registered office address (utility bill + NOC from owner), bank account proof, Memorandum and Articles of Association (drafted), and Digital Signature Certificate (DSC) for each proposed director." },
  { q: "Do I need GST registration immediately after incorporating my company?", a: "GST registration becomes mandatory once turnover crosses ₹40 lakh (₹20 lakh for services). However, Pvt Ltd and LLP companies, inter-state suppliers, and e-commerce sellers must register from Day 1 regardless of turnover. We bundle GST + ROC filing as part of our incorporation package." },
  { q: "What ongoing compliance does a Pvt Ltd company face?", a: "Annual ROC filings (Form AOC-4 and MGT-7) by 30th September and 30th October respectively, board meetings (4 per year, at least one per quarter), AGM (within 6 months of year-end), statutory audit, ITR filing, and TDS/GST monthly returns. Late ROC filing attracts ₹100/day with no cap and strike-off risk after 2 years." },
];

const RELATED = [
  { to: "/compliance-roadmap", label: "Business Compliance Roadmap", description: "Find what your business needs in 60 sec", tag: "Tool" },
  { to: "/gst-registration", label: "GST Registration", description: "Get your GSTIN in 3–7 days", tag: "Service" },
  { to: "/itr-filing", label: "ITR Filing", description: "Annual income tax return for businesses", tag: "Service" },
  { to: "/business-loan-assistance", label: "Business Loan Assistance", description: "MSME loans, working capital, term loans", tag: "Service" },
  { to: "/loan-eligibility-calculator", label: "Loan Eligibility Calculator", description: "Check your business loan eligibility", tag: "Free Tool" },
  { to: "/health-insurance-advisory", label: "Group Health Insurance", description: "Employee group cover for MSMEs", tag: "Service" },
];

const BusinessRegistrationPage = () => {
  useCanonical("/business-registration");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Business Registration in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Complete business registration services including MSME, Startup India, and company incorporation."
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>Business Registration in Coimbatore | MSME, Startup India | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Expert business registration services in Coimbatore. Proprietorship, MSME, Startup India, company incorporation. Hassle-free registration by WINTRUST SOLUTIONS." />
      </Helmet>
      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4">
              <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">Business Registration</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              Business Registration in Coimbatore | MSME & Startup Support
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Starting your entrepreneurial journey begins with the right legal structure. WINTRUST SOLUTIONS offers expert business registration services in Coimbatore, guiding you through the process of setting up your proprietorship, partnership, or company.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary">Register Your Business Today</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Types */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Proprietorship, MSME, and Company Registration
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: <Building size={32} />, title: "Sole Proprietorship", desc: "The simplest structure for solo entrepreneurs. Easy to start, minimal compliance, and full control over your business." },
              { icon: <Users size={32} />, title: "Partnership Firm", desc: "For businesses with two or more partners. Shared responsibility, combined expertise, and easier access to capital." },
              { icon: <Building size={32} />, title: "Private Limited Company", desc: "For startups and businesses seeking to raise funds. Limited liability, separate legal entity, and enhanced credibility." },
              { icon: <Rocket size={32} />, title: "Startup India Registration", desc: "Avail benefits under the government's startup scheme including tax exemptions, easier compliance, and funding access." },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-slate-50 rounded-lg">
                <div className="text-gold mb-4">{item.icon}</div>
                <h3 className="font-semibold text-navy mb-2 text-xl">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MSME Benefits */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold text-navy mb-6 font-heading">
                Benefits of MSME (Udyam) Registration
              </h2>
              <ul className="space-y-4">
                {[
                  "Priority Sector Lending - Lower interest rates from banks",
                  "Government Tenders - Preference in procurement and exemption from EMD",
                  "Protection Against Delayed Payments - Via MSME Samadhaan portal",
                  "Subsidies & Incentives - Access to various government schemes",
                  "Credit Guarantee Scheme - Collateral-free loans up to ₹2 Crore",
                  "Technology Upgradation Support - Under CLCSS scheme",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                    <span className="text-navy">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-navy mb-6">MSME Classification</h3>
              <div className="space-y-4">
                {[
                  { type: "Micro", investment: "Up to ₹1 Crore", turnover: "Up to ₹5 Crore" },
                  { type: "Small", investment: "Up to ₹10 Crore", turnover: "Up to ₹50 Crore" },
                  { type: "Medium", investment: "Up to ₹50 Crore", turnover: "Up to ₹250 Crore" },
                ].map((item, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-semibold text-gold text-lg">{item.type} Enterprise</p>
                    <p className="text-slate-600 text-sm">Investment: {item.investment}</p>
                    <p className="text-slate-600 text-sm">Turnover: {item.turnover}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Documents Required for Business Registration
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "PAN Card of Proprietor/Partners/Directors",
              "Aadhaar Card of all stakeholders",
              "Passport size photographs",
              "Address proof of business premises",
              "Bank account details",
              "NOC from property owner (if rented)",
              "Partnership Deed (for partnerships)",
              "MOA & AOA (for companies)",
            ].map((doc, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <CheckCircle className="text-gold flex-shrink-0" size={20} />
                <span className="text-navy">{doc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gold py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4 font-heading">
            Ready to Start Your Business Journey?
          </h2>
          <p className="text-navy/80 mb-6 max-w-2xl mx-auto">
            Get expert guidance on choosing the right business structure and complete registration support from WINTRUST SOLUTIONS.
          </p>
          <a href="#contact" className="bg-navy text-white px-8 py-3 rounded-md font-semibold hover:bg-navy-900 transition-colors">
            Register Your Business
          </a>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection items={FAQS} testId="biz-reg-faq" />

      {/* Related Resources */}
      <RelatedResources
        title="Related Tools & Services"
        subtitle="Set up your business right from Day 1"
        items={RELATED}
        testId="biz-reg-related"
      />

      <ContactForm />
    </>
  );
};

export default BusinessRegistrationPage;
